import React, { useRef, useEffect, useState } from 'react'
import { MapAreaData, Button, InputField, Label } from '../styling/StyledComponents'
import mapboxgl from 'mapbox-gl';
import Cookies from 'js-cookie';
import { addBuildings } from '../MapLayers/BuildingLayer';
import { LocationData } from '../Geolocation/LocationProvider';
import axios from 'axios'
import { addSourceToHeatmapLayer, createHeatmapData, createHeatmapDataToSource } from '../MapLayers/HeatmapLayer';
import { CSVLink } from "react-csv";

// Predefined color schemes
const COLOR_SCHEMES = {
    "Heat": [
        { stop: 0, color: "rgba(0, 0, 255, 0)" },
        { stop: 0.1, color: "royalblue" },
        { stop: 0.3, color: "cyan" },
        { stop: 0.5, color: "lime" },
        { stop: 0.7, color: "yellow" },
        { stop: 1, color: "red" }
    ],
    "Blues": [
        { stop: 0, color: "rgba(255, 255, 255, 0)" },
        { stop: 0.2, color: "lightcyan" },
        { stop: 0.4, color: "skyblue" },
        { stop: 0.6, color: "deepskyblue" },
        { stop: 0.8, color: "royalblue" },
        { stop: 1, color: "darkblue" }
    ],
    "Greens": [
        { stop: 0, color: "rgba(255, 255, 255, 0)" },
        { stop: 0.2, color: "palegreen" },
        { stop: 0.4, color: "lightgreen" },
        { stop: 0.6, color: "mediumseagreen" },
        { stop: 0.8, color: "seagreen" },
        { stop: 1, color: "darkgreen" }
    ],
    "Purple to Red": [
        { stop: 0, color: "rgba(255, 255, 255, 0)" },
        { stop: 0.2, color: "lavender" },
        { stop: 0.4, color: "mediumpurple" },
        { stop: 0.6, color: "purple" },
        { stop: 0.8, color: "crimson" },
        { stop: 1, color: "darkred" }
    ],
};

function HeatMap({ selectedProjectId, selectedQuestionId }) {
    mapboxgl.accessToken = Cookies.get('mapbox_token')
    const [location, setLocation] = LocationData()
    const [zoom, setZoom] = useState(12.3);
    const [filteredData, setFilteredData] = useState([])
    const [mapReady, setMapReady] = useState(false);
    
    // Heatmap control states
    const [heatmapSettings, setHeatmapSettings] = useState({
        radiusMultiplier: 2.0,
        contourSmoothness: 700,
        opacity: 0.7,
        numShades: 10,
        colorScheme: "Heat",
        showHeatmap: true,
        customColors: false
    });
    
    const mapContainer = useRef(null);
    const map = useRef(null);
    
    // Function to clear heatmap layers and sources
    const clearHeatmapLayer = () => {
        if (map.current && mapReady) {
            if (map.current.getLayer('userdata')) {
                map.current.removeLayer('userdata');
            }
            if (map.current.getSource('userdata')) {
                map.current.removeSource('userdata');
            }
        }
    };

    // Generate color ramp based on selected scheme and number of shades
    const generateColorRamp = () => {
        const baseColors = COLOR_SCHEMES[heatmapSettings.colorScheme] || COLOR_SCHEMES["Heat"];
        const numShades = heatmapSettings.numShades;
        
        if (numShades <= baseColors.length) {
            return baseColors;
        }
        
        // Generate interpolated colors based on number of shades
        const colorRamp = [{ stop: 0, color: baseColors[0].color }];
        
        for (let i = 1; i < numShades - 1; i++) {
            const stop = i / (numShades - 1);
            
            // Find the base color segments to interpolate between
            let lowerIndex = 0;
            for (let j = 0; j < baseColors.length - 1; j++) {
                if (baseColors[j].stop <= stop && baseColors[j + 1].stop >= stop) {
                    lowerIndex = j;
                    break;
                }
            }
            
            const lowerColor = baseColors[lowerIndex];
            const upperColor = baseColors[lowerIndex + 1];
            
            const ratio = (stop - lowerColor.stop) / (upperColor.stop - lowerColor.stop);
            
            colorRamp.push({
                stop: stop,
                color: interpolateColor(lowerColor.color, upperColor.color, ratio)
            });
        }
        
        colorRamp.push({ stop: 1, color: baseColors[baseColors.length - 1].color });
        
        return colorRamp;
    };
    
    // Helper function to interpolate between two colors
    const interpolateColor = (color1, color2, ratio) => {
        // Parse colors to RGBA
        const parseColor = (color) => {
            if (color.startsWith('rgb')) {
                const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)/);
                if (match) {
                    return {
                        r: parseInt(match[1]),
                        g: parseInt(match[2]),
                        b: parseInt(match[3]),
                        a: match[4] ? parseFloat(match[4]) : 1
                    };
                }
            } else {
                // For named colors, create a temporary element to get RGB values
                const temp = document.createElement('div');
                temp.style.color = color;
                document.body.appendChild(temp);
                const computedColor = getComputedStyle(temp).color;
                document.body.removeChild(temp);
                return parseColor(computedColor);
            }
            return { r: 0, g: 0, b: 0, a: 1 }; // Default
        };
        
        const c1 = parseColor(color1);
        const c2 = parseColor(color2);
        
        const r = Math.round(c1.r + (c2.r - c1.r) * ratio);
        const g = Math.round(c1.g + (c2.g - c1.g) * ratio);
        const b = Math.round(c1.b + (c2.b - c1.b) * ratio);
        const a = c1.a + (c2.a - c1.a) * ratio;
        
        return `rgba(${r}, ${g}, ${b}, ${a})`;
    };

    // Update heatmap paint properties when settings change
    useEffect(() => {
        if (!mapReady || !map.current || !map.current.getLayer('userdata')) return;

        const baseRadius = 20; // Base radius value
        map.current.setPaintProperty('userdata', 'heatmap-radius', baseRadius * heatmapSettings.radiusMultiplier);
        map.current.setPaintProperty('userdata', 'heatmap-opacity', heatmapSettings.opacity);
        map.current.setPaintProperty('userdata', 'heatmap-intensity', heatmapSettings.contourSmoothness / 500); // Scale to reasonable intensity
        
        // Update color ramp
        const colorRamp = generateColorRamp();
        const colorExpression = ["interpolate", ["linear"], ["heatmap-density"]];
        colorRamp.forEach(ramp => {
            colorExpression.push(ramp.stop);
            colorExpression.push(ramp.color);
        });
        
        map.current.setPaintProperty('userdata', 'heatmap-color', colorExpression);
        
        // Toggle visibility
        map.current.setLayoutProperty(
            'userdata', 
            'visibility', 
            heatmapSettings.showHeatmap ? 'visible' : 'none'
        );
    }, [heatmapSettings, mapReady]);

    // Initialize map
    useEffect(() => {
        if (!mapboxgl.accessToken || map.current) return;

        try {
            map.current = new mapboxgl.Map({
                container: mapContainer.current,
                style: process.env.REACT_APP_CUSTOM_MAP,
                center: [location.lng, location.lat],
                zoom: zoom,
                pitch: 30,
                dragPan: true,
                doubleClickZoom: true,
                scrollZoom: true,
                dragRotate: true,
            });

            map.current.on('move', () => {
                setLocation({
                    lng: map.current.getCenter().lng.toFixed(4),
                    lat: map.current.getCenter().lat.toFixed(4)
                });
                setZoom(map.current.getZoom().toFixed(2));
            });

            map.current.on('load', () => {
                try {
                    addBuildings(map.current);
                    setMapReady(true);
                } catch (err) {
                    console.error("Error adding buildings:", err);
                }
            });

        } catch (err) {
            console.error("Map initialization error:", err);
            return;
        }

        return () => {
            if (map.current) {
                map.current.remove();
                map.current = null;
                setMapReady(false);
            }
        };
    }, []);

    // Fetch and filter data based on selectedProjectId and selectedQuestionId
    useEffect(() => {
        const fetchData = async () => {
            try {
                if (selectedProjectId) {
                    const response = await axios.post(
                        `${process.env.REACT_APP_BACKEND_URL}/answers/filtered`,
                        {
                            projectId: selectedProjectId,
                            questionId: selectedQuestionId
                        },
                        {
                            headers: {
                                Authorization: `Bearer ${Cookies.get('token')}`
                            },
                            withCredentials: true
                        }
                    );

                    if (response.data.success) {
                        setFilteredData(response.data.result);
                    }
                } else {
                    setFilteredData([]);
                }
            } catch (error) {
                console.error("Error fetching data:", error);
                setFilteredData([]);
            }
        };

        fetchData();
    }, [selectedProjectId, selectedQuestionId]);

    // Update heatmap when filteredData or mapReady changes
    useEffect(() => {
        if (!mapReady || !map.current) return;

        clearHeatmapLayer();
        
        if (filteredData.length > 0) {
            try {
                createHeatmapDataToSource('userdata', map.current, filteredData);
                addSourceToHeatmapLayer('userdata', map.current);
                
                // Apply initial settings
                const baseRadius = 20;
                map.current.setPaintProperty('userdata', 'heatmap-radius', baseRadius * heatmapSettings.radiusMultiplier);
                map.current.setPaintProperty('userdata', 'heatmap-opacity', heatmapSettings.opacity);
                map.current.setPaintProperty('userdata', 'heatmap-intensity', heatmapSettings.contourSmoothness / 500);
                
                // Apply initial color scheme
                const colorRamp = generateColorRamp();
                const colorExpression = ["interpolate", ["linear"], ["heatmap-density"]];
                colorRamp.forEach(ramp => {
                    colorExpression.push(ramp.stop);
                    colorExpression.push(ramp.color);
                });
                
                map.current.setPaintProperty('userdata', 'heatmap-color', colorExpression);
            } catch (err) {
                console.error("Error updating heatmap:", err);
            }
        }
    }, [filteredData, mapReady]);

    // Handle control changes
    const handleSettingChange = (setting, value) => {
        setHeatmapSettings(prev => ({
            ...prev,
            [setting]: value
        }));
    };

    return (
        <div className='fill-height' style={{ position: 'relative' }}>
            <div className='margin-top overlay'>
                <CSVLink 
                    className='text-color padding-m' 
                    data={filteredData} 
                    separator={";"} 
                    filename={"heatmap_data.csv"}
                >
                    <Button>Download</Button>
                </CSVLink>
            </div>
            
            {/* Heatmap Controls Panel */}
            <div style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                background: 'white',
                padding: '15px',
                borderRadius: '8px',
                boxShadow: '0 0 10px rgba(0,0,0,0.2)',
                zIndex: 1,
                maxWidth: '300px'
            }}>
                <h3 style={{ marginTop: 0, color: 'var(--primary-color)' }}>Heatmap Controls</h3>
                
                <div style={{ marginBottom: '15px' }}>
                    <Label>Gaussian Radius Multiplier</Label>
                    <input
                        type="range"
                        min="0.05"
                        max="4.0"
                        step="0.05"
                        value={heatmapSettings.radiusMultiplier}
                        onChange={(e) => handleSettingChange('radiusMultiplier', parseFloat(e.target.value))}
                        style={{ width: '100%', margin: '5px 0' }}
                    />
                    <span>{heatmapSettings.radiusMultiplier.toFixed(2)}</span>
                </div>
                
                <div style={{ marginBottom: '15px' }}>
                    <Label>Contour Smoothness</Label>
                    <input
                        type="range"
                        min="10"
                        max="2000"
                        step="10"
                        value={heatmapSettings.contourSmoothness}
                        onChange={(e) => handleSettingChange('contourSmoothness', parseInt(e.target.value))}
                        style={{ width: '100%', margin: '5px 0' }}
                    />
                    <span>{heatmapSettings.contourSmoothness}</span>
                </div>
                
                <div style={{ marginBottom: '15px' }}>
                    <Label>Heatmap Opacity</Label>
                    <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.05"
                        value={heatmapSettings.opacity}
                        onChange={(e) => handleSettingChange('opacity', parseFloat(e.target.value))}
                        style={{ width: '100%', margin: '5px 0' }}
                    />
                    <span>{heatmapSettings.opacity.toFixed(2)}</span>
                </div>
                
                <div style={{ marginBottom: '15px' }}>
                    <Label>Number of Shades</Label>
                    <input
                        type="range"
                        min="3"
                        max="50"
                        step="1"
                        value={heatmapSettings.numShades}
                        onChange={(e) => handleSettingChange('numShades', parseInt(e.target.value))}
                        style={{ width: '100%', margin: '5px 0' }}
                    />
                    <span>{heatmapSettings.numShades}</span>
                </div>
                
                <div style={{ marginBottom: '15px' }}>
                    <Label>Color Scheme</Label>
                    <select
                        value={heatmapSettings.colorScheme}
                        onChange={(e) => handleSettingChange('colorScheme', e.target.value)}
                        style={{ width: '100%', padding: '5px', margin: '5px 0' }}
                    >
                        {Object.keys(COLOR_SCHEMES).map(scheme => (
                            <option key={scheme} value={scheme}>{scheme}</option>
                        ))}
                    </select>
                </div>
                
                {/* TODO: Add custom color scheme editor here */}
                <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                            type="checkbox"
                            checked={heatmapSettings.showHeatmap}
                            onChange={(e) => handleSettingChange('showHeatmap', e.target.checked)}
                        />
                        Show Heatmap
                    </label>
                </div>
            </div>
            
            <MapAreaData className='fill-height' ref={mapContainer} />
        </div>
    );
}

export default HeatMap;